'use client';

import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ActivityOverlay, BodyText, ErrorPage } from '@rv-life/design-system';
import useOverlaySafetyTimeout from '@/hooks/useOverlaySafetyTimeout';
import useSlowLoadNotice from '@/hooks/useSlowLoadNotice';
import { routes } from '@/routes';
import { getRouteGroup } from '@/utils/match-routes';
import { useAppContext } from '../AppContext';
import css from './MFEsLayout.module.css';

const MFE_ROOT_ID = 'rvlife-mfe-root';

export const RETRY_STATUS_TEXT = {
	idle: '',
	loading: 'Loading the page.',
	loaded: 'Loading has finished.'
} as const;

export const SLOW_LOAD_NOTICE_TEXT = 'Having trouble connecting, still trying';

type RetryStatus = keyof typeof RETRY_STATUS_TEXT;

const goHome = () => window.location.assign(routes.quickLaunch);
const reloadPage = () => window.location.reload();

const MFEsLayout = ({ children }: { children: React.ReactNode }) => {
	const {
		isProcessing,
		authSettled,
		authBootAnswered,
		renderMFE,
		auth,
		garage,
		settings,
		setIsProcessing,
		hasSessionCookies,
		bootAttempt,
		retryBoot
	} = useAppContext();
	const routedMfe = getRouteGroup(usePathname());
	const mfes = { auth, garage, settings };
	// A remount reuses the loaded remote and its stale chunk map, so only a reload recovers.
	const loadFailed = routedMfe !== null && mfes[routedMfe].loadFailed;
	const errorPageRef = useRef<HTMLDivElement>(null);
	const [retryStatus, setRetryStatus] = useState<RetryStatus>('idle');

	// The previous app stays mounted until this page's app mounts, so it must not show here.
	const hidesOtherApp = routedMfe !== null && renderMFE !== null && renderMFE !== routedMfe;
	const routed = routedMfe !== null ? mfes[routedMfe] : null;
	// Between load attempts the page's app is neither mounted, loading nor failed: still one load.
	const betweenAttempts =
		routed !== null && renderMFE !== routedMfe && !routed.loaded && !routed.loadFailed;
	const routedLoading = routed !== null && (routed.isLoading || betweenAttempts);
	// A load the member navigated away from finishes in the background, behind the page they're on.
	const loadingFlags: Record<string, boolean> = { isProcessing };
	if (routedMfe !== null) loadingFlags[routedMfe] = routedLoading;
	const loadingSources = Object.entries(loadingFlags)
		.filter(([, on]) => on)
		.map(([name]) => name)
		.join(', ');
	// A failed load is final, so it also ends the overlay and the boot backstop that would hide it.
	const isLoading = !loadFailed && loadingSources !== '';
	const { hasTimedOut, awaitingRetry } = useOverlaySafetyTimeout({
		isLoading,
		isProcessing,
		routedMfe,
		authSettled,
		setIsProcessing,
		hasSessionCookies,
		bootAttempt,
		loadingSources,
		loadStartedAt: routedLoading ? (routed?.loadStartedAt ?? null) : null
	});
	const showOverlay = isLoading && !hasTimedOut;
	// The backstop can drop the overlay while an MFE bundle or the auth MFE's boot is still pending.
	const authStillWaiting = renderMFE === 'auth' && !authBootAnswered;
	const isWaiting = !awaitingRetry && !loadFailed && (isLoading || authStillWaiting);
	// A spent safety bound is past the notice threshold, so returning to that load shows it at once.
	const showSlowNotice = useSlowLoadNotice(isWaiting) || (isWaiting && hasTimedOut);

	const showErrorPage = awaitingRetry || loadFailed;

	useEffect(() => {
		if (!showErrorPage) return;
		setRetryStatus('idle');
		errorPageRef.current?.querySelector('button')?.focus();
	}, [showErrorPage]);

	useEffect(() => {
		if (retryStatus === 'loading' && !isLoading) setRetryStatus('loaded');
	}, [retryStatus, isLoading]);

	// The Refresh button unmounts with the error page, so focus moves first or drops to <body>.
	const handleRefresh = () => {
		document.getElementById(MFE_ROOT_ID)?.focus();
		setRetryStatus('loading');
		retryBoot();
	};

	return (
		<>
			{showOverlay && <ActivityOverlay />}
			{isWaiting && (
				<div role="status" className={css.slowNotice}>
					{showSlowNotice && <BodyText align="center">{SLOW_LOAD_NOTICE_TEXT}</BodyText>}
				</div>
			)}
			{showErrorPage && (
				<div role="alert" ref={errorPageRef}>
					<ErrorPage
						onRefresh={loadFailed ? reloadPage : handleRefresh}
						onHomeLinkClick={goHome}
					/>
				</div>
			)}
			<div role="status" className={css.statusLine}>
				{RETRY_STATUS_TEXT[retryStatus]}
			</div>
			<div className={css.mfeSlot} hidden={hidesOtherApp}>
				{children}
			</div>
		</>
	);
};

export default MFEsLayout;
